import { Button } from "@components/controls/Button"
import { SortableGrid } from "@components/editor/SortableGrid"
import { displayAlert, displayErrorAlert, useAlertOverlay } from "@hooks/useAlertOverlay"
import { CheckIcon, StarIcon, TrashIcon, UploadIcon } from "@radix-ui/react-icons"
import { useEffect, useMemo, useState } from "react"
import { twMerge } from "tailwind-merge"
import { tv } from "tailwind-variants"
import { v4 } from "uuid"
import { AwardMicroCardFragment, ProfileGalleryItemFragment } from "../../../types.graphql"
import { Scroller } from "@components/layout/Scroller"
import { useUpdateProfileImages } from "@hooks/queries"

type Item = ProfileGalleryItemFragment & {
  upload?: UploadableImage
}

export type UploadableImage = {
  blob: Blob
  blobUrl?: string
  name: string
}

type Props = {
  items: Item[]
  awards: AwardMicroCardFragment[]
  onChange: (items: ProfileGalleryItemFragment[]) => void
}

const createStyles = tv({
  slots: {
    dialog: "w-[95vw] max-w-[100%] md:max-w-[750px] max-h-[100%]",
    toolbar: "stack-x-2 flex-wrap justify-end min-h-[24px] my-2",
    addButton: ["w-full bg-grey-light rounded-sm flex-1"],
  },
})

const gridStyles = tv({
  base: "w-full grid",
  variants: {
    size: {
      large: "grid-cols-3 sm:grid-cols-4 md:grid-cols-4 gap-3",
      small: "grid-cols-4 md:grid-cols-8 gap-1",
    },
  },
  defaultVariants: {
    size: "large",
  },
})

const cardStyles = tv({
  base: "bg-grey/5 group relative flex items-center justify-center el-img:object-contain el-img:object-center el-img:transition-all transition-all rounded-[4px] el-img:block el-img:rounded-[2px] aspect-[4/3] el-img:size-full h-full disabled:!opacity-10 w-full",
  variants: {
    selected: {
      true: "border border-black el-img:scale-90",
      false: "border border-transparent",
    },
    unselected: {
      true: "opacity-50",
    },
  },
})

const MAX_IMAGES = 12
const MAX_WIDTH = 1200

function resizeImages(files: FileList): Promise<UploadableImage[]> {
  function resizeImage(file: File) {
    return new Promise<UploadableImage>((resolve) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement("canvas")
          const ctx = canvas.getContext("2d")!
          canvas.width = Math.min(MAX_WIDTH, img.width)
          canvas.height = (canvas.width / img.width) * img.height
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          canvas.toBlob(
            (blob) => {
              resolve({ blob: blob!, blobUrl: URL.createObjectURL(blob!), name: file.name })
            },
            "image/jpeg",
            0.9
          )
        }
        img.src = e.target?.result as string
      }
      reader.readAsDataURL(file)
    })
  }

  return Promise.all(Array.from(files).map(resizeImage))
}

const blobToBase64 = (blob: Blob) => {
  const reader = new FileReader()
  reader.readAsDataURL(blob)
  return new Promise((resolve) => {
    reader.onloadend = () => {
      resolve((reader.result as string).split(",")[1])
    }
  })
}

export async function saveProfileImages(items: Item[], isBusiness: boolean) {
  const payload = await Promise.all(
    items.map(async (item) => ({
      ...item,
      upload: item.upload?.blob
        ? {
            name: item.upload.name,
            data: await blobToBase64(item.upload.blob),
          }
        : undefined,
    }))
  )
  const result = await useUpdateProfileImages.mutate({
    data: JSON.stringify(payload),
    business: isBusiness,
  })
  return result.updateMyProfileImages?.business! ?? result.updateMyProfileImages?.user!
}

export function PortfolioEdit(props: Props) {
  const alerts = useAlertOverlay()
  const styles = createStyles()
  const [selected, setSelected] = useState<string[]>([])

  const hasSelection = !!selected.length
  const canAddMore = props.items.length < MAX_IMAGES

  props.items.forEach((item) => {
    if (!item.id) item.id = v4()
  })

  const uploadButtons = (
    <>
      <Button size="chip" variant="outline" leftIcon={<UploadIcon />} disabled={!canAddMore}>
        Upload
        <input
          type="file"
          className="appearance-none absolute inset-0 w-full h-full opacity-0 !cursor-pointer"
          accept="image/*"
          multiple
          max={MAX_IMAGES - props.items.length}
          disabled={!canAddMore}
          onChange={(e) => {
            const el = e.currentTarget
            const files = e.currentTarget.files
            if (files?.length) {
              resizeImages(files).then((files) => {
                el.value = ""
                props.onChange(
                  [
                    ...props.items,
                    ...files.map(
                      (upload): Item => ({
                        type: "upload",
                        id: v4(),
                        url: null,
                        awardArtworkIndex: null,
                        awardEntryId: null,
                        caption: null,
                        image: {
                          srcSet: `${upload.blobUrl} 1x`,
                          aspect: null,
                        },
                        upload: upload,
                        attachmentId: null!,
                        uploadImage: null!,
                      })
                    ),
                  ].slice(0, MAX_IMAGES)
                )
              })
            }
          }}
        />
      </Button>
      {props.awards.length > 0 && (
        <Button
          size="chip"
          variant="outline"
          leftIcon={<StarIcon />}
          disabled={!canAddMore}
          onClick={() => {
            let images: Item[] = []
            displayAlert({
              title: "Select Award Images",
              className: "md:max-w-none md:w-auto",
              message: (
                <AwardImageSelector
                  awards={props.awards}
                  onSelect={(selected) => {
                    images = selected
                  }}
                  maxItems={MAX_IMAGES - props.items.length}
                  currentItems={props.items}
                />
              ),
              canCancel: true,
              confirmLabel: "Add",
              onConfirm() {
                props.onChange([...props.items, ...images].slice(0, MAX_IMAGES))
              },
            })
          }}
        >
          Awards
        </Button>
      )}
    </>
  )

  return (
    <div className={styles.dialog()}>
      <div className={styles.toolbar()}>
        {hasSelection && (
          <Button
            variant="outline"
            size="chip"
            leftIcon={<TrashIcon />}
            onClick={() => {
              props.onChange(props.items.filter((item) => !selected.includes(item.id!)))
              setSelected([])
            }}
          >
            Remove Selected
          </Button>
        )}
      </div>
      <Scroller className="max-h-[60vh] w-full">
        <div className="w-full">
          <div className={gridStyles()}>
            <SortableGrid
              items={props.items}
              getId={(item) => item.id!}
              onChange={props.onChange}
              render={(item) => {
                const isSelected = selected.includes(item.id!)
                return (
                  <button
                    type="button"
                    className={twMerge(cardStyles({ selected: isSelected, unselected: hasSelection && !isSelected }), "cursor-grab active:cursor-grabbing")}
                    onClick={() => {
                      setSelected((prev) => {
                        if (isSelected) {
                          return prev.filter((id) => id !== item.id)
                        } else {
                          return [...prev, item.id!]
                        }
                      })
                    }}
                  >
                    <Selector selected={isSelected} />
                    <img draggable={false} srcSet={item.image?.srcSet! ?? item.uploadImage?.srcSet} sizes="140px" />
                  </button>
                )
              }}
            />
            {canAddMore &&
              [...new Array(MAX_IMAGES - props.items.length)].map((_, i) =>
                i === 0 ? (
                  <div className={cardStyles({ className: "flex-col gap-2" })} key={i}>
                    {uploadButtons}
                  </div>
                ) : (
                  <div className={cardStyles({})} key={i} />
                )
              )}
          </div>
        </div>
      </Scroller>
    </div>
  )
}

function Selector({ selected }: { selected: boolean }) {
  return (
    <div
      className={twMerge(
        "absolute z-[2] top-2 left-2 bg-white border border-black text-black size-[16px] rounded-full flex items-center justify-center opacity-0 md:group-hover:opacity-100 cursor-pointer",
        selected && "opacity-100"
      )}
    >
      {selected && <CheckIcon />}
    </div>
  )
}

function AwardImageSelector(props: { awards: AwardMicroCardFragment[]; onSelect: (images: Item[]) => void; maxItems: number; currentItems: Item[] }) {
  const [selected, setSelected] = useState<{ awardId: number; imageIndex: number }[]>([])
  const hasSelection = !!selected.length

  const styles = createStyles()

  // const awards = useMemo(() => {
  //   return props.awards.map((award) => {
  //     return {
  //       ...award,
  //       artworks: award.artworks?.filter((artwork) => !props.currentItems.some((c) => artwork?.srcSet !== c.image?.srcSet!)),
  //     }
  //   }).filter((award) => award.artworks?.length)
  // }, [props.awards, props.currentItems])

  useEffect(() => {
    props.onSelect(
      selected.map((s) => {
        const image = props.awards.find((a) => a.databaseId === s.awardId)?.artworks![s.imageIndex]!
        return {
          type: "award",
          id: v4(),
          url: null,
          awardArtworkIndex: s.imageIndex,
          awardEntryId: s.awardId,
          caption: null,
          attachmentId: null,
          uploadImage: null,
          upload: undefined!,
          image: {
            srcSet: image.srcSet!,
            aspect: null,
          },
        }
      })
    )
  }, [selected])

  return (
    <div className={styles.dialog()}>
      <Scroller className="max-h-[60vh] w-full">
        {props.awards.map((award) => (
          <div key={award.id} className="w-full">
            <div className="type-title-xs-medium mb-2">{award.title}</div>
            <div className={gridStyles({ size: "small", className: "w-full mb-4" })}>
              {award.artworks?.map((artwork, i) => {
                const isSelected = selected.some((s) => s.awardId === award.databaseId! && s.imageIndex === i)
                const isDisabled = props.currentItems.some((item) => item.image?.srcSet === artwork?.srcSet)
                return (
                  <button
                    key={i}
                    type="button"
                    className={cardStyles({
                      selected: isSelected,
                      unselected: hasSelection && !isSelected,
                    })}
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return
                      setSelected((prev) => {
                        if (isSelected) {
                          return prev.filter((s) => s.awardId !== award.databaseId! || s.imageIndex !== i)
                        } else {
                          return [...prev, { awardId: award.databaseId!, imageIndex: i }]
                        }
                      })
                    }}
                  >
                    <img draggable={false} srcSet={artwork?.srcSet!} sizes="140px" />
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </Scroller>
    </div>
  )
}
